import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  CircularProgress,
  Paper
} from '@mui/material';
import axiosInstance from '../api/axiosInstance';

/**
 * Simple Progress Display Component
 * Displays progress data directly without managing WebSocket connections
 */
const SimpleProgressDisplay = ({ 
  progressData,
  title = "Processing...",
  description = "Please wait while the operation completes."
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  // Extract currentImageId early
  const currentImageId = progressData?.currentImageId || null;

  // Fetch thumbnail when currentImageId changes
  useEffect(() => {
    const fetchThumbnail = async () => {
      if (currentImageId) {
        try {
          setThumbnailLoading(true);
          // Fetch with authentication via axios
          const response = await axiosInstance.get(
            `/images/${currentImageId}/thumbnail`,
            { responseType: 'blob' }
          );
          // Create a blob URL for the image
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const url = URL.createObjectURL(blob);
          setThumbnailUrl(url);
          setThumbnailLoading(false);
        } catch (err) {
          console.error('Error fetching image thumbnail:', err);
          setThumbnailLoading(false);
        }
      }
    };

    fetchThumbnail();

    // Clean up blob URL when image changes
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
        setThumbnailUrl(null);
      }
    };
  }, [currentImageId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!progressData) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" my={4}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </Box>
    );
  }

  const {
    currentIndex = 0,
    totalCount = 0,
    currentPhotoName,
    statusMessage = "Processing...",
    percentage = 0,
    lastImageTime = 0,
    averageTime = 0,
    estimatedTimeRemaining = 0,
    skippedCount = 0,
    processedCount = 0
  } = progressData;

  // Format time in a readable way (ms or seconds)
  const formatTime = (ms) => {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    } else {
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      } else {
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
    }
  };

  // Format seconds to HH:MM:SS format
  const formatSeconds = (seconds) => {
    if (seconds < 60) {
      return `00:${seconds.toString().padStart(2, '0')}`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      
      if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
      } else {
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
      }
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3, my: 2 }}>
      <Box display="flex" flexDirection="column" alignItems="center">
        {/* Title */}
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        {/* Description */}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
          {description}
        </Typography>

        {/* Current Image Thumbnail */}
        {currentImageId && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
            {thumbnailLoading ? (
              <CircularProgress size={40} />
            ) : thumbnailUrl ? (
              <img 
                src={thumbnailUrl}
                alt="Current processing" 
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '150px', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  objectFit: 'contain'
                }}
              />
            ) : null}
          </Box>
        )}

        {/* Progress Bar */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={percentage} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }} 
          />
        </Box>

        {/* Progress Text */}
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 'medium' }}>
          {percentage}% Complete
        </Typography>

        {/* Current Progress */}
        {totalCount > 0 && (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Processing {currentIndex} of {totalCount} photos
            </Typography>
            
            {/* Show breakdown if images were skipped */}
            {skippedCount > 0 && (
              <Typography variant="caption" color="success.main" sx={{ mb: 2, display: 'block', textAlign: 'center' }}>
                ✓ {skippedCount} already processed, {processedCount} newly processed
              </Typography>
            )}
          </>
        )}

        {/* Current Photo */}
        {currentPhotoName && (
          <Typography variant="body2" color="primary" sx={{ mb: 2, textAlign: 'center' }}>
            Current: {currentPhotoName}
          </Typography>
        )}

        {/* Status Message */}
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
          {statusMessage}
        </Typography>

        {/* Timing Information */}
        {(lastImageTime > 0 || averageTime > 0 || estimatedTimeRemaining > 0) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)', width: '100%' }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
              ⏱️ Performance Metrics
            </Typography>
            
            {lastImageTime > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
                Last image: {formatTime(lastImageTime)}
              </Typography>
            )}
            
            {averageTime > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
                Average: {formatTime(averageTime)} per image
              </Typography>
            )}
            
            {estimatedTimeRemaining > 0 && (
              <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', fontWeight: 'medium', mt: 0.5 }}>
                Estimated time remaining: {formatSeconds(estimatedTimeRemaining)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default SimpleProgressDisplay;
